import "react-toastify/dist/ReactToastify.css";
import "./style.scss";

import { useEffect } from "react";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useSearchParams,
  useLocation,
} from "react-router-dom";
import { ReactQueryDevtools } from "react-query/devtools";
import { ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import useScrollToTop from "./hooks/useScrollTop";
import usePwaManifest from "./hooks/usePwaManifest";
import { HiOutlineCheckCircle, HiOutlineXCircle } from "react-icons/hi";

import Login from "./routes/auth/Login";
import PassLessConfirmation from "./routes/auth/PassLessConfirmation";
import Register from "./routes/auth/Register";
import ForgotPassword from "./routes/auth/ForgotPassword";
import ResetPassword from "./routes/auth/ResetPassword";
import LevelUpPassword from "./routes/auth/LevelUpPassword";
import RequireAuth from "./components/public/RequireAuth";
import Dashboard from "./routes/Dashboard";
import Layout from "./components/pages/dashboard/Layout";
import Logout from "./routes/auth/Logout";
import Profile from "./routes/Profile";
import ViewEntityUser from "./components/pages/dashboard/entities/ViewEntityUser";
import NotFound from "./routes/NotFound";
import ErrorPagesLayout from "./components/pages/error/ErrorPagesLayout";
import Users from "./routes/Users";
import Billing from "./components/pages/dashboard/Billing";
import Settings from "./components/pages/dashboard/settings/Settings";
import DashboardNotFound from "./components/pages/dashboard/DashboardNotFound";
import AskQuestionMessage from "./components/pages/dashboard/messages/AskQuestionMessage";
import SearchPage from "./routes/Search";
import AdvancedSearch from "./routes/AdvancedSearch";
import Leads from "./routes/Leads";
import Todos from "./routes/Todos";
import Agenda from "./routes/Agenda";
import AiConfig from "./routes/AiConfig";
import Groups from "./routes/Groups";
import RegistrationRouter from "./routes/auth/RegistrationRouter";
import LmcPage from "./components/pages/dashboard/lmc/Index";
import ProcessViewApp from "./components/pages/process/ProcessViewApp";
import LoyaltyProgramApp from "./components/pages/lmcUserVIew/LoyaltyProgramApp";
import LoyaltyProgramDashboardApp from "./components/pages/lmcUserVIew/LoyaltyProgramDashboardApp";
import FeedbackFilterWrapper from "./components/pages/lmcUserVIew/FeedbackFilterWrapper";
import EventApp from "./components/pages/eventView/EventApp";
import EventDashboardApp from "./components/pages/eventView/EventDashboardApp";
import LoginViaLink from "./components/pages/dashboard/loginTemplate/LoginViaLink";
import TempPart from "./components/pages/dashboard/loginTemplate/TempPart";
import AuthLayout from "./components/pages/auth/AuthLayout";
import UserTracker from "./components/pages/dashboard/entities/UserTracker";

import "./i18n";
import { getForcedLanguage } from "./utils/languageUtility";
import ViewEntityGroup from "./routes/ViewEntityGroup";
import UserResetPassword from "./routes/user/ResetPassword";
import UTM from "./components/public/UTM";
import ConsentManager from "./components/common/consentManager/ConsentManager";
import UserNotes from "./components/pages/dashboard/entities/UserNotes";
import ROIGuarantee from "./components/pages/dashboard/ROIGuarantee";
import ROIGuaranteeSuccess from "./components/pages/dashboard/ROIGuaranteeSuccess";
import BusinessCard from "./routes/BusinessCard";
import BusinessCardApp from "./components/pages/businessCardView/BusinessCardApp";
import { GenericAuthProvider } from "./contexts/AuthContext";
import System from "./routes/System";
import Providers from "./routes/Providers";
import Monitoring from "./routes/Monitoring";
import ImpersonationEnded from "./routes/ImpersonationEnded";
import AiEngineMonitoring from "./routes/AiEngineMonitoring";
import Paymod from "./routes/Paymod";
import Partners from "./routes/Partners";
import Security from "./routes/Security";
import PaymentModule from "./routes/PaymentModule";
import EventPayments from "./routes/EventPayments";
import ESignature from "./routes/ESignature";
import StripeConnectSuccess from "./routes/StripeConnectSuccess";
import StripeConnectError from "./routes/StripeConnectError";
import CustomRegistrationPage from "./components/pages/dashboard/customRegistration";
import SignupFormsListPage from "./components/pages/dashboard/signupForms/SignupFormsListPage";
import CustomRegister from "./components/pages/customRegister/CustomRegister";
import CreditResult from "./components/pages/dashboard/CreditResult";
import { RoleProtectedRoute } from "./components/RoleProtectedRoute";
import { Cap } from "./utils/rbacCatalog";
import RegistrationComplete from "./routes/RegistrationComplete";
import LinkUnavailable from "./routes/LinkUnavailable";
import SigningPage from "./components/pages/signing/SigningPage";
import BookingTunnelPage from "./components/pages/booking/BookingTunnelPage";
import BookingPage from "./components/pages/booking/BookingPage";
import UnsubscribePage from "./routes/UnsubscribePage";
import VerificationPage from "./components/pages/verification/VerificationPage";
import VerificationLookupPage from "./components/pages/verification/VerificationLookupPage";
import { usePublicPageDirection } from "./hooks/usePublicPageDirection";

function App() {
  const { i18n, t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  useScrollToTop();
  usePwaManifest();

  useEffect(() => {
    const langue = getForcedLanguage(searchParams);
    if (!langue) return;

    if (i18n.language !== langue) {
      i18n.changeLanguage(langue);
    }
  }, [searchParams, i18n]);

  useEffect(() => {
    const STORAGE_KEY = "samsung_browser_alert_dismissed";
    const ONE_HOUR_MS = 60 * 60 * 1000;
    const isSamsungBrowser = /samsungbrowser/i.test(navigator.userAgent);
    const dismissedAt = localStorage.getItem(STORAGE_KEY);
    const isExpired =
      !dismissedAt || Date.now() - Number(dismissedAt) > ONE_HOUR_MS;

    if (isSamsungBrowser && isExpired) {
      alert(
        `${t("samsungBrowserAlert.title")}\n\n${t(
          "samsungBrowserAlert.message"
        )}`
      );
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    }
  }, [i18n.language, t]);

  usePublicPageDirection();

  const isBusinessCardRoute = location.pathname.startsWith("/business-card/");

  return (
    <div className="App">
      <Routes>
        <Route path="/" element={<Navigate to={"control-panel"} />} />

        <Route path="impersonation-ended" element={<ImpersonationEnded />} />

        <Route
          path="user/:entity_identification/registration"
          element={<RegistrationRouter />}
        />

        <Route
          path="process/:processId/*"
          element={
            <GenericAuthProvider>
              <ProcessViewApp />
            </GenericAuthProvider>
          }
        />

        <Route
          path="loyalty-program/:processId/dashboard/*"
          element={
            <GenericAuthProvider>
              <LoyaltyProgramDashboardApp />
            </GenericAuthProvider>
          }
        />
        <Route
          path="loyalty-program/:processId/feedback"
          element={
            <GenericAuthProvider>
              <FeedbackFilterWrapper />
            </GenericAuthProvider>
          }
        />
        <Route
          path="loyalty-program/:processId/*"
          element={
            <GenericAuthProvider>
              <LoyaltyProgramApp />
            </GenericAuthProvider>
          }
        />

        <Route
          path="event/:processId/dashboard/*"
          element={
            <GenericAuthProvider>
              <EventDashboardApp />
            </GenericAuthProvider>
          }
        />
        <Route
          path="event/:processId/*"
          element={
            <GenericAuthProvider>
              <EventApp />
            </GenericAuthProvider>
          }
        />

        <Route
          path="business-card/:processId/*"
          element={
            <GenericAuthProvider>
              <BusinessCardApp />
            </GenericAuthProvider>
          }
        />

        <Route
          path="custom-registration/:processId/*"
          element={
            <GenericAuthProvider>
              <CustomRegister />
            </GenericAuthProvider>
          }
        />

        <Route
          path="unsubscribe/:entityId/:userId/:token"
          element={<UnsubscribePage />}
        />

        <Route path="sign/:entityId/:userId/:token" element={<SigningPage />} />

        <Route
          path="sign/verify/:entityId/:userId/:requestId"
          element={<VerificationPage />}
        />

        <Route path="sign/verify" element={<VerificationLookupPage />} />

        <Route path="booking/:entityId" element={<BookingPage />} />
        <Route
          path="booking/:entityId/:typeId"
          element={<BookingTunnelPage />}
        />

        <Route
          path="registration-complete"
          element={<RegistrationComplete />}
        />

        <Route path="link-unavailable" element={<LinkUnavailable />} />

        <Route path="user/reset-password" element={<UserResetPassword />} />

        <Route
          path="passless/confirmation"
          element={<PassLessConfirmation />}
        />

        <Route
          path="login/:login_code"
          element={<LoginViaLink externalPage />}
        />

        <Route
          element={
            <AuthLayout>
              <Outlet />
            </AuthLayout>
          }
        >
          <Route path="login" element={<Login />} />
          <Route path="register" element={<Register />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route path="level-up-password" element={<LevelUpPassword />} />
          <Route path="logout" element={<Logout />} />
        </Route>

        <Route
          path="control-panel"
          element={
            <RequireAuth>
              <Layout>
                <Outlet />
              </Layout>
            </RequireAuth>
          }
        >
          <Route index element={<Dashboard />} />
          <Route
            path="users/:user_identification"
            element={
              <RoleProtectedRoute requireCapability={Cap.USERS_DETAILS}>
                <ViewEntityUser />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="users/:user_identification/history"
            element={
              <RoleProtectedRoute requireCapability={Cap.USERS_ACTIVITIES}>
                <UserTracker isRoute />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="users/:user_identification/notes"
            element={
              <RoleProtectedRoute requireCapability={Cap.USER_NOTES}>
                <UserNotes isRoute />
              </RoleProtectedRoute>
            }
          />

          <Route
            path="users"
            element={
              <RoleProtectedRoute requireCapability={Cap.CUSTOMERS_LIST}>
                <Users />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="leads"
            element={
              <RoleProtectedRoute requireCapability={Cap.LEADS}>
                <Leads />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="todos/*"
            element={
              <RoleProtectedRoute requireCapability={Cap.TASKS_BOARDS}>
                <Todos />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="agenda"
            element={
              <RoleProtectedRoute requireCapability={Cap.USERS_AGENDA}>
                <Agenda />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="groups"
            element={
              <RoleProtectedRoute requireCapability={Cap.GROUP_MANAGEMENT}>
                <Groups />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="groups/:group_identification"
            element={
              <RoleProtectedRoute requireCapability={Cap.GROUP_MANAGEMENT}>
                <ViewEntityGroup />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="settings"
            element={
              <RoleProtectedRoute
                requireAnyCapability={[
                  { capability: Cap.ENTITY_SETTINGS },
                  { capability: Cap.APIS },
                  { capability: Cap.ROLE_MANAGEMENT },
                  { capability: Cap.EXPORT_DATA },
                ]}
              >
                <Settings />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="profile"
            element={
              <RoleProtectedRoute>
                <Profile />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="billing"
            element={
              <RoleProtectedRoute requireOwner>
                <Billing />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="lmc"
            element={
              <RoleProtectedRoute requireCapability={Cap.LOYALTY}>
                <LmcPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="business-card"
            element={
              <RoleProtectedRoute requireCapability={Cap.BUSINESS_CARDS}>
                <BusinessCard />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="signup-forms"
            element={
              <RoleProtectedRoute requireCapability={Cap.SIGNUP_FORMS}>
                <SignupFormsListPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="signup-forms/:formId"
            element={
              <RoleProtectedRoute requireCapability={Cap.SIGNUP_FORMS}>
                <CustomRegistrationPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="login-template"
            element={
              <RoleProtectedRoute requireCapability={Cap.APIS}>
                <TempPart />
              </RoleProtectedRoute>
            }
          />
          <Route path="support" element={<AskQuestionMessage />} />
          <Route
            path="roi-guarantee"
            element={
              <RoleProtectedRoute requireOwner>
                <ROIGuarantee />
              </RoleProtectedRoute>
            }
          />

          <Route
            path="roi-guarantee/success"
            element={<ROIGuaranteeSuccess />}
          />
          <Route path="credit/result" element={<CreditResult />} />
          <Route
            path="search"
            element={
              <RoleProtectedRoute requireCapability={Cap.SEARCH}>
                <SearchPage />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="advanced-search"
            element={
              <RoleProtectedRoute requireCapability={Cap.ADVANCED_SEARCH}>
                <AdvancedSearch />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="ai-config"
            element={
              <RoleProtectedRoute requireCapability={Cap.AI_ENGINE_CONFIG}>
                <AiConfig />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="system"
            element={
              <RoleProtectedRoute>
                <System />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="providers"
            element={
              <RoleProtectedRoute>
                <Providers />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="monitoring"
            element={
              <RoleProtectedRoute>
                <Monitoring />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="paymod"
            element={
              <RoleProtectedRoute>
                <Paymod />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="partners"
            element={
              <RoleProtectedRoute>
                <Partners />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="security"
            element={
              <RoleProtectedRoute>
                <Security />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="ai-engine-monitoring"
            element={
              <RoleProtectedRoute>
                <AiEngineMonitoring />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="payment-module"
            element={
              <RoleProtectedRoute requireCapability={Cap.PAYMENT_CONFIG}>
                <PaymentModule />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="payment-module/stripe-connect/success"
            element={<StripeConnectSuccess />}
          />
          <Route
            path="payment-module/stripe-connect/error"
            element={<StripeConnectError />}
          />
          <Route
            path="event-payments"
            element={
              <RoleProtectedRoute requireCapability={Cap.EVENTS}>
                <EventPayments />
              </RoleProtectedRoute>
            }
          />
          <Route
            path="e-signature"
            element={
              <RoleProtectedRoute requireCapability={Cap.ESIGNATURE_CONFIG}>
                <ESignature />
              </RoleProtectedRoute>
            }
          />
          <Route path="*" element={<DashboardNotFound />} />
        </Route>

        <Route
          element={
            <ErrorPagesLayout>
              <Outlet />
            </ErrorPagesLayout>
          }
        >
          <Route path="/not-found" element={<NotFound />} />
        </Route>

        <Route path="*" element={<Navigate to={"/not-found"} />} />
      </Routes>

      {!isBusinessCardRoute && (
        <ToastContainer
          icon={({ type }) => {
            if (type === "success")
              return (
                <HiOutlineCheckCircle className="text-green-600" size={50} />
              );
            if (type === "error")
              return <HiOutlineXCircle className="text-red-600" size={50} />;
            else return null;
          }}
        />
      )}
      <ReactQueryDevtools initialIsOpen={false} />

      <UTM />
      <ConsentManager />
    </div>
  );
}

export default App;
